import React, { useState, useEffect } from "react";
import { Form } from "react-bootstrap";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import './DayEditor.css';

const TIME_PLACEHOLDER = '--:--';

const DayEditor = ({ selectedDate, formattedDate, onPrev, onNext }) => {
  const { t } = useTranslation();
  
  const timeBalances = useSelector((state) => state.timeBalances?.items || []);

  const dateStr = selectedDate 
    ? `${selectedDate.getFullYear()}-${String(selectedDate.getMonth() + 1).padStart(2, '0')}-${String(selectedDate.getDate()).padStart(2, '0')}`
    : '';

  const currentRow = timeBalances.find((tb) => tb.date === dateStr);

  const [checkIn, setCheckIn] = useState(TIME_PLACEHOLDER);
  const [checkOut, setCheckOut] = useState(TIME_PLACEHOLDER);
  const [pauses, setPauses] = useState([]);
  const [projects, setProjects] = useState([]);
  const [workedHours, setWorkedHours] = useState('0h 0m');

  const todayStr = new Date().toISOString().split('T')[0];
  const isToday = dateStr === todayStr;

  useEffect(() => {
    if (currentRow) {
      setCheckIn(currentRow.clock_in ? String(currentRow.clock_in).substring(0, 5) : TIME_PLACEHOLDER);
      setCheckOut(currentRow.clock_out ? String(currentRow.clock_out).substring(0, 5) : TIME_PLACEHOLDER);
      
      const normalizedPauses = (currentRow.pauses || []).map(p => ({
        start: p.start ? String(p.start).substring(0, 5) : TIME_PLACEHOLDER,
        end: p.end ? String(p.end).substring(0, 5) : TIME_PLACEHOLDER,
        reason: p.reason || 'lunch'
      }));
      setPauses(normalizedPauses);
      setProjects(currentRow.projects || []);
      
      const h = Math.floor((currentRow.worked_minutes || 0) / 60);
      const m = (currentRow.worked_minutes || 0) % 60;
      setWorkedHours(`${h}h ${m}m`);
    } else {
      setCheckIn(TIME_PLACEHOLDER);
      setCheckOut(TIME_PLACEHOLDER);
      setPauses([]);
      setProjects([]);
      setWorkedHours('0h 0m');
    }
  }, [currentRow, selectedDate]);

  const isInProgress = isToday && checkIn !== TIME_PLACEHOLDER && checkOut === TIME_PLACEHOLDER;

  return (
    <div className="day-editor-card d-flex flex-column" style={{ height: "100%", maxHeight: "427px" }}>
      <div className="editor-header text-center mb-3 position-relative" style={{ flexShrink: 0 }}>
        
        {isInProgress && (
          <div className="position-absolute top-0 end-0 mt-1 me-2 d-flex align-items-center gap-1">
            <div 
              style={{
                width: "8px", 
                height: "8px", 
                backgroundColor: "#10b981", 
                borderRadius: "50%",
                boxShadow: "0 0 8px #10b981",
                animation: "pulse 2s infinite"
              }}
            />
            <span style={{ fontSize: "11px", color: "#10b981", fontWeight: "bold" }}>
              {t('components.DayEditor.inProgress')}
            </span>
          </div>
        )}

        <div className="d-flex align-items-center justify-content-center gap-3 mb-2">
          <button className="btn btn-sm btn-link p-0 text-decoration-none fs-5 fw-bold" onClick={onPrev}>‹</button>
          <h6 className="txt-main m-0 fw-bold" style={{ fontSize: "15px" }}>{formattedDate}</h6>
          <button className="btn btn-sm btn-link p-0 text-decoration-none fs-5 fw-bold" onClick={onNext}>›</button>
        </div>
        <h4 className="editor-title m-0 fw-normal">{workedHours}</h4>
        <small className="editor-subtitle text-muted text-uppercase" style={{ fontSize: "10px", letterSpacing: "0.5px" }}>
          {t('components.DayEditor.workedHours')}
        </small>
      </div>

      <div className="shifts-container custom-scrollbar" style={{ flex: 1, overflowY: "auto", overflowX: "hidden", paddingRight: "8px" }}>
        
        <div className="border rounded p-3 mb-3 bg-surface">
          <div className="text-muted mb-2 d-flex align-items-center gap-1" style={{ fontSize: "12px" }}>
            <span>🕒</span> <span>{t('components.DayEditor.workShift')}</span>
          </div>
          <div className="d-flex gap-2 align-items-center flex-nowrap">
            <Form.Control type="text" value={checkIn} disabled className="text-center bg-surface" />
            <span className="text-muted">{t('components.DayEditor.to')}</span>
            <Form.Control type="text" value={checkOut} disabled className="text-center bg-surface" />
          </div>
        </div>

        {pauses.length > 0 && (
          <div className="d-flex flex-column mb-3">
            {pauses.map((p, index) => (
              <div 
                key={index} 
                className="border rounded p-2 mb-2 d-flex align-items-center gap-2 flex-nowrap" 
                style={{ backgroundColor: 'var(--gray-100)' }}
              >
                <span style={{ fontSize: "14px", flexShrink: 0 }}>☕</span>
                <Form.Control type="text" size="sm" value={p.start} disabled className="text-center bg-surface" style={{ minWidth: "65px" }} />
                <span className="txt-subtle" style={{ flexShrink: 0 }}>{t('components.DayEditor.to')}</span>
                <Form.Control type="text" size="sm" value={p.end} disabled className="text-center bg-surface" style={{ minWidth: "65px" }} />
                <Form.Select size="sm" value={p.reason} disabled className="bg-surface" style={{ flex: 1, minWidth: "110px" }}>
                  <option value="lunch">{t('components.ClockCard.reasons.lunch', 'Almoço')}</option>
                  <option value="personal_matters">{t('components.ClockCard.reasons.personal_matters', 'Assuntos Pessoais')}</option>
                  <option value="meeting">{t('components.ClockCard.reasons.meeting', 'Reunião')}</option>
                  <option value="other">{t('components.ClockCard.reasons.other', 'Outro')}</option>
                </Form.Select>
              </div>
            ))}
          </div>
        )}

        {projects.length > 0 && (
          <div className="border rounded p-3 bg-surface mb-2">
             <div className="text-muted mb-2 d-flex align-items-center gap-1" style={{ fontSize: "12px" }}>
                <span>📁</span> <span>{t('components.DayEditor.workedProjects')}</span>
             </div>
             {projects.map((proj, idx) => (
                <div key={idx} className="d-flex justify-content-between align-items-center border-bottom pb-1 mb-2 last-child-no-border">
                   <div className="d-flex align-items-center gap-2 text-truncate" style={{ maxWidth: "70%" }}>
                      <div style={{ width: "8px", height: "8px", borderRadius: "50%", backgroundColor: proj.color || "#64748b", flexShrink: 0 }} />
                      <span style={{ fontSize: "13px", fontWeight: "500" }} className="text-truncate">{proj.name}</span>
                   </div>
                   <span className="text-muted fw-bold" style={{ fontSize: "12px", flexShrink: 0 }}>
                      {Math.floor((proj.hours_logged * 60 || proj.minutes || 0) / 60)}h {(Math.round(proj.hours_logged * 60) || proj.minutes || 0) % 60}m
                   </span>
                </div>
             ))}
          </div>
        )}

      </div>
    </div>
  );
};

export default DayEditor;